import { type FC } from "react";
import cn from "classnames";
import { CustomButton } from "~/components";
import { ConfigNamesFields, NamesOfFieldEnum, OrgTypeEnum, ResponseDefaultFields } from "~/typings/types";

import styles from "./ModalContent.module.scss";

function isTopManager(value: unknown): value is { full_name?: string } {
  return (
    value !== null &&
    typeof value === "object" &&
    "full_name" in value
  );
}

interface Props {
  fields?: ResponseDefaultFields['fields'] | null;
  configNameFields: ConfigNamesFields;
  handleClickSuccess: () => void;
  handleClickReject: () => void;
  orgType: OrgTypeEnum;
}

export const ModalContent: FC<Props> = ({
  fields,
  handleClickSuccess,
  handleClickReject,
  configNameFields,
  orgType
}) => {
  if (!fields) {
    return (
      <div className={styles.containerError}>
        <p className={styles.textError}>Данные не найдены</p>
        <div className={styles.footerError}>
          <CustomButton
            name="Понятно"
            type="button"
            onClick={handleClickReject}
            className={cn(styles.button)}
          />
        </div>
      </div>
    );
  }

  const renderName = (name: string | undefined) => {
    if (!name) {
      return null;
    }
    return (
      <h4 style={{ fontWeight: "bold" }}>
        {orgType === OrgTypeEnum.IndividualEntrepreneur
          ? `${name}, это Вы?`
          : `${name}, это Ваша организация?`}
      </h4>
    );
  };

  const renderContent = (fields: ResponseDefaultFields['fields']) => {
    return (Object.entries(fields) as [NamesOfFieldEnum, string][]).map(
      ([nameField, valueField]) => {
        // безопасно проверяем top_manager для всех типов организаций
        if (nameField === "top_manager" && isTopManager(valueField)) {
          return (
            <div key={nameField} className={styles.fieldsRow}>
              <p className={styles.firstColumn}>{configNameFields?.top_manager}</p>
              <p className={styles.secondColumn}>{valueField.full_name || ""}</p>
            </div>
          );
        }

        // Пропускаем поле 'name' и поля, которых нет в configNameFields
        if (nameField === NamesOfFieldEnum.Name || !(nameField in configNameFields)) {
          return null;
        }

        // Проверяем, что valueField не является объектом
        if (typeof valueField === 'object' && valueField !== null) {
          return null;
        }

        return (
          <div key={nameField} className={styles.fieldsRow}>
            <p className={styles.firstColumn}>{configNameFields[nameField]}</p>
            <p className={styles.secondColumn}>{valueField}</p>
          </div>
        );
      }
    ).filter(Boolean);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div>{renderName(fields[NamesOfFieldEnum.Name])}</div>
        <div>{renderContent(fields)}</div>
      </div>
      <div className={styles.footer}>
        <CustomButton
          name="Да, правильно"
          type="button"
          onClick={handleClickSuccess}
          className={cn(styles.button)}
        />
        <CustomButton
          name="Нет"
          type="button"
          onClick={handleClickReject}
          className={cn(styles.button, styles.buttonBack)}
        />
      </div>
    </div>
  );
};